import React, { useState } from 'react';
import { DollarSign, Calendar, RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';
import CountdownTimer from './CountdownTimer';
import { toast } from 'react-hot-toast';
import { formatCurrency } from '@cosbyte/utils';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import VerificationRequiredModal from './verification/VerificationRequiredModal';
import { isSecondaryVerified, isSecondaryVerificationError } from '../utils/verificationGate';

const partyId = (party, fallbackId) => (party && (party.id || party._id)) || fallbackId || null;

const formatPrice = (amount, currency) => {
    const value = Number(amount);
    if (!Number.isFinite(value)) return '';
    try {
        return formatCurrency(value, currency || 'GHS');
    } catch {
        return `${currency || ''} ${value.toLocaleString()}`.trim();
    }
};

/**
 * CustomOfferCard Component
 * Displays custom offer in chat messages
 * Status: pending, accepted, declined, expired, cancelled
 */
const CustomOfferCard = ({ offer, currentUserId, onUpdate }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [declineNote, setDeclineNote] = useState('');
    const [showDeclineInput, setShowDeclineInput] = useState(false);

    // Either party can be missing from the payload (an older message, a deleted account); the card
    // still renders and falls back to the flat ids the API always sends.
    const offeredToId = partyId(offer.offeredTo, offer.offeredToId);
    const offeredById = partyId(offer.offeredBy, offer.offeredById);
    const isRecipient = !!currentUserId && offeredToId === currentUserId;
    const isSender = !!currentUserId && offeredById === currentUserId;
    const senderUsername = offer.offeredBy?.username;

    // The API sends the offer's fields flat (title, displayAmount, deliveryDays...); older payloads
    // nested them under `offer.offer` with a `pricing` object.
    const details = offer.offer || {};
    const title = details.title ?? offer.title;
    const description = details.description ?? offer.description;
    const deliveryDays = details.deliveryDays ?? offer.deliveryDays;
    const revisions = details.revisions ?? offer.revisions ?? 0;
    const requirements = details.requirements ?? offer.requirements ?? [];
    const priceCurrency = details.pricing?.displayCurrency || offer.displayCurrency || offer.baseCurrency;
    const priceAmount = details.pricing?.displayAmount ?? offer.displayAmount ?? offer.baseAmount;
    const isPending = offer.status === 'pending';
    const isExpired = offer.status === 'expired' || new Date(offer.expiresAt) < new Date();

    const handleAccept = async () => {
        // Accepting creates the order and holds the escrow, so it needs Secondary; the server enforces it too
        if (user && !isSecondaryVerified(user)) {
            setShowVerificationModal(true);
            return;
        }
        try {
            setLoading(true);
            const response = await api.patch(`/custom-offers/${offer.id || offer._id}/accept`);

            toast.success('Offer accepted! Order created successfully.');

            if (onUpdate) {
                onUpdate(response.data.data);
            }

            // Navigate to order page if order was created
            if (response.data.data.order) {
                // window.location.href = `/orders/${response.data.data.order.id || response.data.data.order._id}`;
            }
        } catch (error) {
            if (isSecondaryVerificationError(error)) {
                setShowVerificationModal(true);
            } else {
                toast.error(error.response?.data?.message || 'Failed to accept offer');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDecline = async () => {
        if (!declineNote.trim()) {
            toast.error('Please provide a reason for declining');
            return;
        }

        try {
            setLoading(true);
            await api.patch(`/custom-offers/${offer.id || offer._id}/decline`, { note: declineNote });

            toast.success('Offer declined');
            setShowDeclineInput(false);

            if (onUpdate) {
                onUpdate({ ...offer, status: 'declined', response: { note: declineNote } });
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to decline offer');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async () => {
        try {
            setLoading(true);
            await api.patch(`/custom-offers/${offer.id || offer._id}/cancel`, { reason: 'Cancelled by sender' });

            toast.success('Offer cancelled');

            if (onUpdate) {
                onUpdate({ ...offer, status: 'cancelled' });
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to cancel offer');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = () => {
        const badges = {
            pending: { color: 'bg-blue-100 text-blue-600', text: 'Pending' },
            accepted: { color: 'bg-green-100 text-green-600', text: 'Accepted' },
            declined: { color: 'bg-gray-100 text-gray-600', text: 'Declined' },
            expired: { color: 'bg-red-100 text-red-600', text: 'Expired' },
            cancelled: { color: 'bg-gray-100 text-gray-600', text: 'Cancelled' }
        };

        const status = isExpired && isPending ? 'expired' : offer.status;
        const badge = badges[status] || badges.pending;

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                {badge.text}
            </span>
        );
    };

    const getBorderColor = () => {
        if (isExpired || offer.status === 'expired') return 'border-red-300';
        if (offer.status === 'accepted') return 'border-green-300';
        if (offer.status === 'declined' || offer.status === 'cancelled') return 'border-gray-300';
        return 'border-blue-300';
    };

    return (
        <div className={`border-2 ${getBorderColor()} rounded-lg p-4 bg-white shadow-sm max-w-md`}>
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-900">Custom Offer</h4>
                        {senderUsername && <p className="text-xs text-gray-500">from @{senderUsername}</p>}
                    </div>
                </div>
                {getStatusBadge()}
            </div>

            {/* Content */}
            <div className="space-y-2 mb-3">
                <h3 className="font-semibold text-lg text-gray-900">{title}</h3>
                <p className="text-sm text-gray-600 line-clamp-3">{description}</p>
            </div>

            {/* Details */}
            <div className="grid grid-cols-3 gap-3 mb-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-1">
                    <DollarSign className="w-4 h-4 text-gray-600" />
                    <div>
                        <p className="text-xs text-gray-500">Price</p>
                        <p className="font-semibold text-sm">
                            {formatPrice(priceAmount, priceCurrency)}
                        </p>
                    </div>
                </div>

                <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4 text-gray-600" />
                    <div>
                        <p className="text-xs text-gray-500">Delivery</p>
                        <p className="font-semibold text-sm">{deliveryDays} days</p>
                    </div>
                </div>

                <div className="flex items-center space-x-1">
                    <RefreshCw className="w-4 h-4 text-gray-600" />
                    <div>
                        <p className="text-xs text-gray-500">Revisions</p>
                        <p className="font-semibold text-sm">{revisions}</p>
                    </div>
                </div>
            </div>

            {/* Requirements */}
            {requirements.length > 0 && (
                <div className="mb-3">
                    <p className="text-xs font-semibold text-gray-700 mb-1">Requirements:</p>
                    <ul className="list-disc list-inside text-xs text-gray-600 space-y-0.5">
                        {requirements.slice(0, 3).map((req, idx) => (
                            <li key={idx}>{req}</li>
                        ))}
                        {requirements.length > 3 && (
                            <li className="text-blue-600">+{requirements.length - 3} more</li>
                        )}
                    </ul>
                </div>
            )}

            {/* Countdown / Status Message */}
            {isPending && !isExpired && (
                <div className="mb-3">
                    <CountdownTimer
                        targetDate={offer.expiresAt}
                        type="expiration"
                        onExpire={() => {
                            if (onUpdate) {
                                onUpdate({ ...offer, status: 'expired' });
                            }
                        }}
                    />
                </div>
            )}

            {offer.status === 'accepted' && offer.order && (
                <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded">
                    <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <p className="text-sm text-green-700">
                            Order {offer.order.orderNumber || 'created'} successfully!
                        </p>
                    </div>
                </div>
            )}

            {(offer.status === 'declined' || offer.status === 'cancelled') && offer.response?.note && (
                <div className="mb-3 p-2 bg-gray-50 border border-gray-200 rounded">
                    <p className="text-xs text-gray-500 mb-1">Reason:</p>
                    <p className="text-sm text-gray-700">{offer.response.note}</p>
                </div>
            )}

            {isExpired && isPending && (
                <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded">
                    <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-red-600" />
                        <p className="text-sm text-red-700">This offer has expired</p>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            {isRecipient && isPending && !isExpired && (
                <div className="space-y-2">
                    {!showDeclineInput ? (
                        <div className="flex space-x-2">
                            <button
                                onClick={handleAccept}
                                disabled={loading}
                                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm flex items-center justify-center space-x-1"
                            >
                                <CheckCircle className="w-4 h-4" />
                                <span>{loading ? 'Accepting...' : 'Accept Offer'}</span>
                            </button>
                            <button
                                onClick={() => setShowDeclineInput(true)}
                                disabled={loading}
                                className="flex-1 px-4 py-2 border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-50 font-medium text-sm flex items-center justify-center space-x-1"
                            >
                                <XCircle className="w-4 h-4" />
                                <span>Decline</span>
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <textarea
                                value={declineNote}
                                onChange={(e) => setDeclineNote(e.target.value)}
                                placeholder="Please provide a reason for declining..."
                                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                rows="2"
                            />
                            <div className="flex space-x-2">
                                <button
                                    onClick={handleDecline}
                                    disabled={loading || !declineNote.trim()}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                                >
                                    {loading ? 'Declining...' : 'Confirm Decline'}
                                </button>
                                <button
                                    onClick={() => setShowDeclineInput(false)}
                                    disabled={loading}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {isSender && isPending && !isExpired && (
                <button
                    onClick={handleCancel}
                    disabled={loading}
                    className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                >
                    {loading ? 'Cancelling...' : 'Cancel Offer'}
                </button>
            )}

            {showVerificationModal && (
                <VerificationRequiredModal
                    isOpen
                    onClose={() => setShowVerificationModal(false)}
                    returnUrl={window.location.pathname + window.location.search}
                />
            )}
        </div>
    );
};

export default CustomOfferCard;
